import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PageSEO from './PageSEO';
import Header from './Header';
import Footer from './Footer';
import { useAuth } from '../context/AuthContext';
import { LIKERT_OPTIONS, YES_NO_OPTIONS, calculateResult, CATEGORIES } from '../data/seoTestsConfig';
import { findClusterForTest } from '../data/clusterConfig';
import { trackTestStart, trackTestComplete } from '../utils/analytics';
import {
  ChevronRight, ChevronLeft, CheckCircle, AlertTriangle,
  Lock, Crown, Sparkles, ArrowRight, Brain, Home,
  Clock, BarChart3, Shield, Target, FileText, Activity, Users
} from 'lucide-react';

const SITE_URL = 'https://doktorcebinde.com';

/* ── Deterministic completion count per slug (10k-85k) ── */
const getCompletionCount = (slug) => {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = ((h << 5) - h + slug.charCodeAt(i)) | 0;
  return 10000 + Math.abs(h % 75000);
};
const formatCount = (n) => n.toLocaleString('tr-TR');

/* ── Breadcrumb ── */
const Breadcrumb = ({ testConfig }) => {
  const cat = CATEGORIES[testConfig.category];
  const cluster = findClusterForTest(testConfig.slug);
  return (
    <nav className="max-w-4xl mx-auto px-6 pt-6 pb-2" aria-label="breadcrumb" data-testid="seo-breadcrumb">
      <ol className="flex items-center gap-1.5 flex-wrap text-xs">
        <li className="flex items-center gap-1">
          <Home className="w-3 h-3" style={{ color: '#64748B' }} />
          <Link to="/" style={{ color: '#64748B' }} className="hover:text-cyan-400 transition-colors">Ana Sayfa</Link>
        </li>
        <li style={{ color: '#475569' }}>/</li>
        {cluster ? (
          <>
            <li><Link to={`/${cluster.slug}`} style={{ color: '#94A3B8' }} className="hover:text-cyan-400 transition-colors">{cluster.heading}</Link></li>
            <li style={{ color: '#475569' }}>/</li>
          </>
        ) : cat ? (
          <>
            <li><span style={{ color: '#94A3B8' }}>{cat.label}</span></li>
            <li style={{ color: '#475569' }}>/</li>
          </>
        ) : null}
        <li><span style={{ color: '#E2E8F0' }} className="font-medium">{testConfig.heading}</span></li>
      </ol>
    </nav>
  );
};

/* ── Schema Markup ── */
const SchemaMarkup = ({ testConfig }) => {
  const cat = CATEGORIES[testConfig.category];
  const pageUrl = `${SITE_URL}/${testConfig.slug}`;
  const schemas = [
    { '@context': 'https://schema.org', '@type': 'Article', headline: testConfig.heading, description: testConfig.metaDescription, url: pageUrl, author: { '@type': 'Organization', name: 'DoktorCebinde' }, publisher: { '@type': 'Organization', name: 'DoktorCebinde', url: SITE_URL }, mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl }, articleSection: cat?.label || 'Sağlık', inLanguage: 'tr' },
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: SITE_URL }, ...(cat ? [{ '@type': 'ListItem', position: 2, name: cat.label, item: `${SITE_URL}/#${cat.slug}` }] : []), { '@type': 'ListItem', position: cat ? 3 : 2, name: testConfig.heading, item: pageUrl }] },
  ];
  if (testConfig.faq?.length) schemas.push({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: testConfig.faq.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) });
  return <Helmet>{schemas.map((s, i) => <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>)}</Helmet>;
};

/* ── FAQ ── */
const FAQSection = ({ testConfig }) => {
  const faq = testConfig.faq;
  if (!faq?.length) return null;
  return (
    <section className="mt-12 mb-6" data-testid="seo-faq">
      <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
        <FileText className="w-5 h-5" style={{ color: '#D9A258' }} /> Sık Sorulan Sorular
      </h3>
      <div className="space-y-3">
        {faq.map((f, i) => (
          <details key={i} className="group overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
            <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none select-none">
              <span className="text-sm font-semibold text-white pr-4">{f.q}</span>
              <ChevronRight className="w-4 h-4 shrink-0 transition-transform group-open:rotate-90" style={{ color: '#D9A258' }} />
            </summary>
            <div className="px-5 pb-4"><p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>{f.a}</p></div>
          </details>
        ))}
      </div>
    </section>
  );
};

/* ── SEO Content ── */
const SEOContent = ({ testConfig }) => {
  const c = testConfig.seoContent;
  if (!c) return null;
  const icons = [Activity, Shield, Target];
  const colors = ['#22D3EE', '#D88A61', '#5A9E7A'];
  const sections = [
    { title: 'Bu durum nedir?', text: c.whatIsIt },
    { title: 'Ne zaman ciddiye alınmalı?', text: c.whenSerious },
    { title: 'Bu test neyi ölçer?', text: c.whatMeasures },
  ].filter(s => s.text);
  if (!sections.length) return null;
  return (
    <section className="mt-12 mb-6" data-testid="seo-content">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sections.map((s, i) => {
          const Icon = icons[i] || Activity;
          return (
            <div key={i} className="p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4" style={{ color: colors[i] }} />
                <h4 className="text-sm font-bold text-white">{s.title}</h4>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.50)' }}>{s.text}</p>
            </div>
          );
        })}
      </div>
      <p className="text-xs mt-4 text-center" style={{ color: 'rgba(255,255,255,0.2)' }}>Klinik araştırmalarla desteklenen bilimsel bir tarama aracıdır.</p>
    </section>
  );
};

/* ── Inline Hint ── */
const InlineHint = ({ testConfig }) => {
  if (!testConfig.inlineHint) return null;
  const { inlineHint, inlineLinks } = testConfig;
  let parts = [inlineHint];
  if (inlineLinks?.length) {
    for (const link of inlineLinks) {
      const newParts = [];
      for (const part of parts) {
        if (typeof part === 'string' && part.includes(link.text)) {
          const idx = part.indexOf(link.text);
          if (idx > 0) newParts.push(part.substring(0, idx));
          newParts.push({ text: link.text, href: link.href });
          const rest = part.substring(idx + link.text.length);
          if (rest) newParts.push(rest);
        } else newParts.push(part);
      }
      parts = newParts;
    }
  }
  return (
    <div className="p-5 mb-8" style={{ background: 'rgba(34,211,238,0.04)', border: '1px solid rgba(34,211,238,0.10)', borderRadius: 8 }} data-testid="seo-inline-hint">
      <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
        {parts.map((part, i) => typeof part === 'string' ? <span key={i}>{part}</span> : <Link key={i} to={part.href} className="font-medium underline underline-offset-2 transition-colors hover:text-cyan-300" style={{ color: '#67E8F9' }}>{part.text}</Link>)}
      </p>
    </div>
  );
};

/* ── Related Tests ── */
const RELATED_COLORS = [
  { accent: '#22D3EE', bg: 'rgba(34,211,238,0.05)', border: 'rgba(34,211,238,0.12)' },
  { accent: '#D9A258', bg: 'rgba(217,162,88,0.05)', border: 'rgba(217,162,88,0.12)' },
  { accent: '#34D399', bg: 'rgba(52,211,153,0.05)', border: 'rgba(52,211,153,0.12)' },
  { accent: '#A78BFA', bg: 'rgba(167,139,250,0.05)', border: 'rgba(167,139,250,0.12)' },
  { accent: '#F87171', bg: 'rgba(248,113,113,0.05)', border: 'rgba(248,113,113,0.12)' },
  { accent: '#38BDF8', bg: 'rgba(56,189,248,0.05)', border: 'rgba(56,189,248,0.12)' },
];

const RelatedTests = ({ testConfig }) => {
  const related = testConfig.relatedTests;
  const cluster = findClusterForTest(testConfig.slug);
  return (
    <section className="mt-12 mb-6" data-testid="seo-related-tests">
      {cluster && (
        <Link to={`/${cluster.slug}`} className="flex items-center gap-2 mb-5 px-4 py-3 transition-all hover:-translate-y-0.5"
          style={{ background: 'rgba(34,211,238,0.04)', border: '1px solid rgba(34,211,238,0.10)', borderRadius: 8 }} data-testid="seo-back-to-hub">
          <ChevronRight className="w-4 h-4 rotate-180" style={{ color: '#67E8F9' }} />
          <span className="text-sm font-medium" style={{ color: '#67E8F9' }}>Diğer {cluster.heading.toLowerCase()}ni görmek için tıklayın</span>
        </Link>
      )}
      {related?.length > 0 && (
        <>
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" style={{ color: '#6F90B1' }} /> Benzer Testler
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {related.map((r, i) => {
              const c = RELATED_COLORS[i % RELATED_COLORS.length];
              return (
                <Link key={i} to={`/${r.slug}`} className="flex items-center gap-3 px-4 py-3.5 transition-all hover:-translate-y-0.5"
                  style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8 }} data-testid={`seo-related-${r.slug}`}>
                  <div className="w-2 h-2 shrink-0" style={{ background: c.accent, borderRadius: 2 }} />
                  <span className="text-sm font-medium" style={{ color: c.accent }}>{r.label}</span>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};

/* ══════════════════════════════════════════ */
export default function SEOTestPage({ testConfig }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState('intro');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [details, setDetails] = useState({});
  const [result, setResult] = useState(null);

  const questions = testConfig.questions;
  const totalQ = questions.length;
  const progress = ((currentQ + 1) / totalQ) * 100;

  const handleAnswer = (qId, value) => setAnswers(prev => ({ ...prev, [qId]: value }));
  const handleNext = () => {
    if (currentQ < totalQ - 1) setCurrentQ(prev => prev + 1);
    else { const res = calculateResult(answers, testConfig); trackTestComplete(testConfig.slug, res.totalScore, res.level); setResult(res); setStep('result'); }
  };
  const handlePrev = () => { if (currentQ > 0) setCurrentQ(prev => prev - 1); };
  const currentQuestion = questions[currentQ];
  const currentAnswer = answers[currentQuestion?.id];

  /* ─── INTRO ─── */
  if (step === 'intro') {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <PageSEO title={testConfig.title} description={testConfig.metaDescription} />
        <SchemaMarkup testConfig={testConfig} />
        <Header />
        <Breadcrumb testConfig={testConfig} />
        <main className="max-w-4xl mx-auto px-6 py-8 sm:py-12">
          {/* Hero — 2 col on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-10">
            {/* Left — main info */}
            <div className="lg:col-span-3">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.15)', borderRadius: 8 }}>
                  <Brain className="w-6 h-6" style={{ color: '#67E8F9' }} />
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: '#94A3B8' }}>{CATEGORIES[testConfig.category]?.label || 'Değerlendirme'}</p>
                  <p className="text-xs" style={{ color: '#64748B' }}>{totalQ} soru · ~{Math.ceil(totalQ * 0.4)} dakika</p>
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4 leading-tight" data-testid="seo-test-title">
                {testConfig.heading}
              </h1>
              <p className="text-base leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {testConfig.subtitle}
              </p>
              <button onClick={() => { trackTestStart(testConfig.slug); setStep('test'); }}
                className="flex items-center gap-2 px-8 py-3.5 rounded text-sm font-bold transition-all hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, #FACC15, #F59E0B)', color: '#1A2B3C', boxShadow: '0 6px 20px rgba(250,204,21,0.25)' }}
                data-testid="seo-test-start-btn">
                Teste Başla <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Right — stats card */}
            <div className="lg:col-span-2">
              <div className="p-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
                <div className="space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: 'rgba(250,204,21,0.08)', borderRadius: 8 }}>
                      <BarChart3 className="w-5 h-5" style={{ color: '#FACC15' }} />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{totalQ}</p>
                      <p className="text-xs" style={{ color: '#94A3B8' }}>Soru sayısı</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.08)', borderRadius: 8 }}>
                      <Clock className="w-5 h-5" style={{ color: '#22D3EE' }} />
                    </div>
                    <div>
                      <p className="text-xl font-bold" style={{ color: '#22D3EE' }}>~{Math.ceil(totalQ * 0.4)} dk</p>
                      <p className="text-xs" style={{ color: '#94A3B8' }}>Tahmini süre</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.08)', borderRadius: 8 }}>
                      <CheckCircle className="w-5 h-5" style={{ color: '#34D399' }} />
                    </div>
                    <div>
                      <p className="text-xl font-bold" style={{ color: '#34D399' }}>Anında</p>
                      <p className="text-xs" style={{ color: '#94A3B8' }}>Sonuç ve değerlendirme</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: 'rgba(167,139,250,0.08)', borderRadius: 8 }}>
                      <Users className="w-5 h-5" style={{ color: '#A78BFA' }} />
                    </div>
                    <div>
                      <p className="text-xl font-bold" style={{ color: '#A78BFA' }}>{formatCount(getCompletionCount(testConfig.slug))}</p>
                      <p className="text-xs" style={{ color: '#94A3B8' }}>Kişi tamamladı</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <InlineHint testConfig={testConfig} />
          <SEOContent testConfig={testConfig} />
          <FAQSection testConfig={testConfig} />
          <RelatedTests testConfig={testConfig} />
        </main>
        <Footer />
      </div>
    );
  }

  /* ─── TEST ─── */
  if (step === 'test') {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <PageSEO title={`${testConfig.title} - Soru ${currentQ + 1}`} />
        <Header />
        <main className="max-w-3xl mx-auto px-6 py-8 sm:py-12">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium" style={{ color: '#94A3B8' }}>Soru {currentQ + 1} / {totalQ}</span>
              <span className="text-xs font-bold" style={{ color: '#FACC15' }}>%{Math.round(progress)}</span>
            </div>
            <div className="h-1.5 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
              <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #FACC15, #F59E0B)', borderRadius: 2 }} />
            </div>
          </div>

          {/* Question Card */}
          <div className="p-6 sm:p-8 mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }} data-testid="seo-test-question-card">
            <p className="text-lg sm:text-xl font-semibold text-white mb-8 leading-relaxed" data-testid="seo-test-question-text">
              {currentQuestion.text}
            </p>
            <div className="space-y-3">
              {(currentQuestion.yesNo ? YES_NO_OPTIONS : LIKERT_OPTIONS).map(opt => (
                <button key={opt.value} onClick={() => handleAnswer(currentQuestion.id, opt.value)}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left transition-all"
                  style={{
                    background: currentAnswer === opt.value ? 'rgba(250,204,21,0.08)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${currentAnswer === opt.value ? 'rgba(250,204,21,0.25)' : 'rgba(255,255,255,0.05)'}`,
                    borderRadius: 6,
                  }}
                  data-testid={`seo-test-option-${opt.value}`}>
                  <div className="w-5 h-5 flex items-center justify-center shrink-0 transition-all" style={{
                    border: `2px solid ${currentAnswer === opt.value ? '#FACC15' : 'rgba(255,255,255,0.15)'}`,
                    borderRadius: 4, background: currentAnswer === opt.value ? '#FACC15' : 'transparent',
                  }}>
                    {currentAnswer === opt.value && <CheckCircle className="w-3 h-3 text-black" />}
                  </div>
                  <span className="text-sm font-medium" style={{ color: currentAnswer === opt.value ? '#FEF3C7' : '#94A3B8' }}>{opt.label}</span>
                </button>
              ))}
            </div>

            {currentQuestion.conditionalOptions && currentAnswer === 3 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-white mb-2">{currentQuestion.conditionalLabel || 'Lütfen seçin:'}</p>
                <div className="flex flex-wrap gap-2">
                  {currentQuestion.conditionalOptions.map((opt, i) => (
                    <button key={i} onClick={() => setDetails(prev => ({ ...prev, [currentQuestion.id + '_option']: opt }))}
                      className="px-3 py-2 text-xs font-medium transition-all"
                      style={{ background: details[currentQuestion.id + '_option'] === opt ? 'rgba(250,204,21,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${details[currentQuestion.id + '_option'] === opt ? 'rgba(250,204,21,0.25)' : 'rgba(255,255,255,0.06)'}`, color: details[currentQuestion.id + '_option'] === opt ? '#FACC15' : '#94A3B8', borderRadius: 4 }}
                      data-testid={`seo-test-conditional-${i}`}>{opt}</button>
                  ))}
                </div>
              </div>
            )}

            {currentQuestion.hasDetail && ((currentQuestion.yesNo && currentAnswer === 3) || (!currentQuestion.yesNo && currentAnswer >= 2)) && (
              <div className="mt-4">
                <textarea value={details[currentQuestion.id] || ''} onChange={e => setDetails(prev => ({ ...prev, [currentQuestion.id]: e.target.value }))}
                  placeholder={currentQuestion.detailPlaceholder || 'Lütfen açıklayın...'} rows={3}
                  className="w-full outline-none resize-none text-sm"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: '#C8D0E0', padding: '12px 16px', borderRadius: 6 }}
                  data-testid="seo-test-detail-textarea" />
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex gap-3">
            {currentQ > 0 && (
              <button onClick={handlePrev} className="flex items-center gap-2 px-5 py-3 rounded text-sm font-medium transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8' }}
                data-testid="seo-test-prev-btn"><ChevronLeft className="w-4 h-4" /> Önceki</button>
            )}
            <button onClick={handleNext} disabled={currentAnswer === undefined}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded text-sm font-bold transition-all hover:scale-[1.02] disabled:opacity-30"
              style={{ background: 'linear-gradient(135deg, #FACC15, #F59E0B)', color: '#1A2B3C' }}
              data-testid="seo-test-next-btn">
              {currentQ < totalQ - 1 ? 'Sonraki' : 'Sonucu Gör'} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  /* ─── RESULT ─── */
  return (
    <div className="min-h-screen" style={{ background: 'transparent' }}>
      <PageSEO title={`${testConfig.title} - Sonuç`} />
      <SchemaMarkup testConfig={testConfig} />
      <Header />
      <Breadcrumb testConfig={testConfig} />
      <main className="max-w-4xl mx-auto px-6 py-8 sm:py-10">
        {/* Score — 2 col */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-10">
          <div className="lg:col-span-2 flex flex-col items-center justify-center">
            <div className="relative w-32 h-32 mx-auto mb-4">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
                <circle cx="50" cy="50" r="42" fill="none" stroke={result.result.color} strokeWidth="6"
                  strokeLinecap="round" strokeDasharray={`${result.percentage * 2.64} 264`}
                  style={{ filter: `drop-shadow(0 0 8px ${result.result.color}40)` }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-white">{result.totalScore}</span>
                <span className="text-xs" style={{ color: '#94A3B8' }}>/ {result.maxScore}</span>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-2" style={{ background: `${result.result.color}15`, border: `1px solid ${result.result.color}30`, borderRadius: 4 }}>
              <CheckCircle className="w-4 h-4" style={{ color: result.result.color }} />
              <span className="text-sm font-bold" style={{ color: result.result.color }} data-testid="seo-test-result-label">{result.result.label}</span>
            </div>
            <h2 className="text-xl font-bold text-white">Sonucunuz Hazır</h2>
          </div>

          <div className="lg:col-span-3">
            {/* Result text */}
            <div className="p-6 mb-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }} data-testid="seo-test-result-card">
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>{result.result.text}</p>
            </div>

            {result.showWarning && (
              <div className="p-5 mb-4 flex items-start gap-3" style={{ background: 'rgba(250,204,21,0.05)', border: '1px solid rgba(250,204,21,0.12)', borderRadius: 8 }} data-testid="seo-test-warning">
                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: '#FACC15' }} />
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{result.warningText}</p>
              </div>
            )}

            {/* CTA */}
            {user ? (
              <div className="space-y-3">
                <button onClick={() => navigate('/premium')}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded text-base font-bold transition-all hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg, #FACC15, #F59E0B)', color: '#1A2B3C' }}
                  data-testid="seo-test-premium-btn">
                  <Crown className="w-5 h-5" /> Premium ile Detaylı Analiz Al
                </button>
                <button onClick={() => navigate('/dashboard')}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded text-sm font-medium transition-all"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: '#94A3B8' }}
                  data-testid="seo-test-dashboard-btn">
                  Panelime Dön <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <button onClick={() => navigate('/register')}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded text-base font-bold transition-all hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg, #FACC15, #F59E0B)', color: '#1A2B3C' }}
                  data-testid="seo-test-register-btn">
                  <Sparkles className="w-5 h-5" /> Ücretsiz Kayıt Ol ve Devam Et
                </button>
                <button onClick={() => navigate('/login')}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded text-sm font-medium transition-all"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: '#94A3B8' }}
                  data-testid="seo-test-login-btn">
                  Zaten hesabınız var mı? <ArrowRight className="w-4 h-4" /> Giriş Yapın
                </button>
              </div>
            )}
          </div>
        </div>

        <SEOContent testConfig={testConfig} />
        <FAQSection testConfig={testConfig} />
        <RelatedTests testConfig={testConfig} />
      </main>
      <Footer />
    </div>
  );
}
